// Subtle notification chime — synthesized with the Web Audio API so nothing is
// bundled or fetched (CSP-safe, works offline). The on/off preference is a
// per-device UI setting persisted in localStorage (default on), separate from
// the server-side per-category delivery preferences.

const STORAGE_KEY = 'nexilink.notif.sound';

export function isNotificationSoundEnabled(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY) !== 'off';
    } catch {
        return true; // default on when storage is unavailable
    }
}

export function setNotificationSoundEnabled(on: boolean): void {
    try {
        localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
    } catch {
        /* ignore — best-effort persistence */
    }
}

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
    try {
        const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AC) return null;
        if (!ctx) ctx = new AC();
        return ctx;
    } catch {
        return null;
    }
}

// Schedule the actual notes on a running context: a soft two-note rise
// (~G5 → C6) at a low gain with quick fades — a gentle "blip-bloop".
function scheduleChime(audio: AudioContext): void {
    const now = audio.currentTime;
    const peak = 0.09; // subtle, but audible on laptop speakers
    const notes = [
        { freq: 784.0, at: 0 },     // G5
        { freq: 1046.5, at: 0.11 }, // C6
    ];
    for (const n of notes) {
        const osc = audio.createOscillator();
        const gain = audio.createGain();
        osc.type = 'sine';
        osc.frequency.value = n.freq;
        const start = now + n.at;
        const dur = 0.2;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.linearRampToValueAtTime(peak, start + 0.02); // soft attack (no click)
        gain.gain.exponentialRampToValueAtTime(0.0001, start + dur); // gentle decay
        osc.connect(gain).connect(audio.destination);
        osc.start(start);
        osc.stop(start + dur + 0.02);
    }
}

// Play the chime. Pass force=true to bypass the enabled check (the settings
// preview / "Testa" button). Browser autoplay policy keeps the context
// suspended until a user gesture, so we resume() FIRST and only schedule once
// the timeline is actually running — otherwise the notes fire into a paused
// clock and nothing is heard. An arrival before any page interaction may still
// be silent (no gesture to unlock audio); that's an unavoidable browser rule.
export function playNotificationChime(force = false): void {
    if (!force && !isNotificationSoundEnabled()) return;
    const audio = getCtx();
    if (!audio) return;
    if (audio.state === 'suspended') {
        audio.resume().then(() => scheduleChime(audio)).catch(() => { /* blocked until a gesture */ });
    } else {
        scheduleChime(audio);
    }
}
